// widget.tsx — entry point.
//
// Two ways to use the widget:
//
// 1. Embed script (preferred for non-React sites — WordPress, Squarespace, etc.):
//
//    <script src="https://chat.acceltra.ai/widget.js"
//            data-firm-name="NG Sidhu Law"
//            data-chat-host="https://chat.acceltra.ai"
//            async defer></script>
//
//    The script auto-mounts a launcher button + window in a Shadow DOM
//    on the bottom-right of the page, isolated from the host's CSS.
//
// 2. Programmatic (for React/Next.js sites — `import { mountAcceltraWidget }`):
//
//    import { mountAcceltraWidget } from "@acceltra/widget";
//    mountAcceltraWidget({
//      firmName: "NG Sidhu Law",
//      chatHost: "https://chat.acceltra.ai",
//      mountTo: document.getElementById("widget-root"),
//    });
//
// The widget is built as a single ~20KB gzipped IIFE bundle. CSS is
// inlined and injected into a Shadow DOM root so it doesn't leak.

import { h, render } from "preact";
import { useState, useCallback, useEffect, useRef } from "preact/hooks";
import { ChatWindow } from "./components/ChatWindow";
import { captureSource, sourceOverridesFromDataset, type SourceInfo } from "./source";
import {
  DEFAULT_THEME,
  fetchTheme,
  loadCachedTheme,
  saveCachedTheme,
  themeCssVars,
  type WidgetTheme,
  type WidgetIcon,
} from "./theme";
import { hasActiveChat, hasEndedSession } from "./chat-persistence";

// Import CSS as raw text so we can inject it into the Shadow DOM root.
// Vite's `?inline` query suffix returns the CSS as a string at build time.
import styles from "./styles.css?inline";

export interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
  voice: boolean;
  // True while a live voice caption is still being transcribed (interim).
  // Cleared when the segment is finalized. Used only for voice transcripts.
  interim?: boolean;
}

interface MountOptions {
  firmName: string;
  /** Opaque per-firm widget key (data-firm-token). Preferred firm identity:
   *  stable across renames, not enumerable. firmName stays for display + as a
   *  legacy resolution fallback. */
  firmToken?: string;
  chatHost: string;
  // Launch style:
  //  - "bubble" (default): floating launcher pill, bottom-right, opens a popup.
  //  - "inline": the chat panel renders directly inside `mountTo`, sized to fill
  //    it — for pairing on a website hero as an always-visible online
  //    receptionist. Requires `mountTo`.
  mode?: "bubble" | "inline";
  mountTo?: HTMLElement;        // optional for bubble; required for inline
  startOpen?: boolean;          // open the window immediately (bubble only; for testing)
  // Optional Supabase access_token. Only set by the dashboard's test-chat
  // page when a signed-in firm_demo account is mounting the widget. The
  // chat-service validates the token server-side; if it resolves to a
  // firm_demo account the session is marked demo_mode and all outbound
  // side-effects are skipped. The production embed never sets this.
  demoToken?: string;
  /** Source/attribution (contract v1.2), captured at load. */
  source?: SourceInfo;
  /** Theme override — drives branding directly and skips the /chat/config
   *  fetch. Used by the dashboard live preview; the public embed never sets it. */
  theme?: Partial<WidgetTheme>;
  /** Initial brand-color hints from the embed tag (data-primary-color /
   *  data-accent-color) so even a first visit paints in the firm's colors.
   *  Hints only: the localStorage cache and the fetched theme both win, so a
   *  stale snippet self-heals on the next fetch. */
  themeHint?: Partial<WidgetTheme>;
}

// Support-headset icon for the launcher button. Built from clean primitive
// shapes (arc band + two filled ear cups + mic boom) rather than a single
// complex path, so the rendering is crisp at any size and the silhouette
// reads as "headset" instantly.
function SupportIcon({ size = 22 }: { size?: number }): preact.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Headband arc — top of the headset */}
      <path
        d="M4 13a8 8 0 0 1 16 0"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        fill="none"
      />
      {/* Left ear cup */}
      <rect
        x="3"
        y="12.5"
        width="4.5"
        height="7"
        rx="1.5"
        fill="currentColor"
      />
      {/* Right ear cup */}
      <rect
        x="16.5"
        y="12.5"
        width="4.5"
        height="7"
        rx="1.5"
        fill="currentColor"
      />
      {/* Mic boom — curves down from right cup */}
      <path
        d="M18.75 19.5v0.75a2.25 2.25 0 0 1-2.25 2.25H14"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
      {/* Mic mouthpiece */}
      <circle cx="13" cy="22.5" r="1.1" fill="currentColor" />
    </svg>
  );
}

// Built-in launcher glyphs the firm can pick in Settings. headset is the
// stock SupportIcon; the rest are simple, crisp single-color marks.
function ThemedIcon({ icon, size = 22 }: { icon: WidgetIcon; size?: number }): preact.JSX.Element {
  if (icon === "headset") return <SupportIcon size={size} />;
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": 2,
    "stroke-linecap": "round" as const,
    "stroke-linejoin": "round" as const,
    "aria-hidden": "true" as const,
  };
  if (icon === "chat") {
    return (
      <svg {...common}>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    );
  }
  if (icon === "scales") {
    return (
      <svg {...common}>
        <path d="M12 3v18M5 21h14M7 7l-4 7a4 4 0 0 0 8 0L7 7zm10 0l-4 7a4 4 0 0 0 8 0l-4-7zM7 7l5-2 5 2" />
      </svg>
    );
  }
  // sparkles
  return (
    <svg {...common}>
      <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
      <path d="M19 14l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z" />
    </svg>
  );
}

function LauncherIcon({ theme, size = 20 }: { theme: WidgetTheme; size?: number }): preact.JSX.Element {
  // The launcher shows the receptionist avatar (a human face invites the
  // click) or the firm's picked glyph. Deliberately NOT the firm logo: the
  // logo only replaces the letter-initials fallback in the header/hero/nudge —
  // it must never hijack the launcher or dead-end the icon picker in Settings.
  if (theme.avatar_url) {
    return <img class="acc-launcher-icon-img" src={theme.avatar_url} alt="" />;
  }
  return <ThemedIcon icon={theme.icon} size={size} />;
}

// Proactive teaser that peeks from the launcher a few seconds in — a rep
// greeting (avatar + name + first-person line) to invite an anxious visitor to
// start. Whole card opens the chat; the × dismisses for the session.
function NudgeBubble({
  message,
  repName,
  avatar,
  initials,
  onOpen,
  onDismiss,
}: {
  message: string;
  repName: string;
  avatar: string | null;
  initials: string;
  onOpen: () => void;
  onDismiss: () => void;
}): preact.JSX.Element {
  return (
    <div class="acc-nudge">
      {/* Stretched overlay button = the whole card opens chat; the close × is a
          SIBLING (not nested) so both are clean, separate controls for a11y. */}
      <button type="button" class="acc-nudge-open" aria-label="Open chat" onClick={onOpen} />
      <span class="acc-nudge-avatar" aria-hidden="true">
        {avatar ? <img src={avatar} alt="" /> : initials}
      </span>
      <div class="acc-nudge-body">
        <span class="acc-nudge-name">{repName}</span>
        <span class="acc-nudge-text">{message}</span>
      </div>
      <button
        type="button"
        class="acc-nudge-close"
        aria-label="Dismiss"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        ×
      </button>
    </div>
  );
}

function App({
  firmName,
  firmToken,
  chatHost,
  mode,
  startOpen,
  demoToken,
  source,
  themeOverride,
  themeHint,
}: {
  firmName: string;
  firmToken?: string;
  chatHost: string;
  mode: "bubble" | "inline";
  startOpen: boolean;
  demoToken?: string;
  source?: SourceInfo;
  // When set, drives the theme directly and SKIPS the network fetch — used by
  // the dashboard's live branding preview to reflect unsaved edits instantly.
  themeOverride?: Partial<WidgetTheme>;
  // Embed-tag color hints for a cold first visit (see MountOptions.themeHint).
  themeHint?: Partial<WidgetTheme>;
}): preact.JSX.Element | null {
  const inline = mode === "inline";
  // Inline panel is always "open" (it IS the page element). Bubble starts
  // closed unless startOpen.
  const [open, setOpen] = useState(inline || startOpen);
  // First paint must never be the default navy when a firm theme exists, so
  // the theme is seeded SYNCHRONOUSLY before the first render: the dashboard
  // preview's override, else the cached last-fetched theme (repeat visits),
  // else the embed tag's color hints (first visit). `ready` gates rendering:
  // a truly cold first visit (nothing to seed from) renders NOTHING until
  // /chat/config resolves or a short timeout passes — a launcher appearing
  // ~300ms late is invisible to visitors; one that changes color is not.
  const [boot] = useState(() => {
    if (themeOverride) return { theme: { ...DEFAULT_THEME, ...themeOverride }, ready: true };
    const cached = loadCachedTheme(firmToken || firmName);
    if (cached) return { theme: cached, ready: true };
    return {
      theme: themeHint ? { ...DEFAULT_THEME, ...themeHint } : DEFAULT_THEME,
      ready: !!themeHint,
    };
  });
  const [theme, setTheme] = useState<WidgetTheme>(boot.theme);
  const [ready, setReady] = useState<boolean>(boot.ready);
  // Inline mode has no "close" — the × / End-conversation instead restarts the
  // panel back to its welcome screen by remounting ChatWindow under a new key.
  const [resetKey, setResetKey] = useState(0);
  const [nudge, setNudge] = useState(false);
  // Bubble-mode minimize-out: `closing` keeps the window mounted while the
  // acc-minimize exit animation plays, then the timeout unmounts it. The
  // window would otherwise vanish the same frame the × is clicked.
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const launcherRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => {
    if (inline) {
      setResetKey((k) => k + 1);
      return;
    }
    const finish = () => {
      setClosing(false);
      setOpen(false);
      // Return focus to the launcher (it reappears) for keyboard/SR users.
      requestAnimationFrame(() => launcherRef.current?.focus());
    };
    // Reduced motion: no exit animation, so don't hold the window for one.
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      finish();
      return;
    }
    setClosing(true);
    window.clearTimeout(closeTimer.current);
    // Slightly past the 0.16s animation; `forwards` holds the last frame so
    // a late timeout can never flash the window back at full size.
    closeTimer.current = window.setTimeout(finish, 180);
  }, [inline]);

  const dismissNudge = useCallback(() => {
    setNudge(false);
    try {
      sessionStorage.setItem(`acc_nudge_${firmName}`, "1");
    } catch {
      /* sessionStorage may be blocked */
    }
  }, [firmName]);
  const openFromNudge = useCallback(() => {
    setNudge(false);
    setOpen(true);
  }, []);

  // Branding: when a themeOverride is supplied (dashboard live preview), apply
  // it directly and skip the fetch — so edits reflect instantly. Otherwise pull
  // the firm's saved branding once at mount (fetchTheme never throws) and cache
  // it for the next visit's first paint. A failed fetch (null) keeps whatever
  // is showing — cache, hint, or defaults — and never touches the cache.
  const overrideKey = themeOverride ? JSON.stringify(themeOverride) : null;
  useEffect(() => {
    if (themeOverride) {
      setTheme({ ...DEFAULT_THEME, ...themeOverride });
      return;
    }
    let live = true;
    void fetchTheme(chatHost, firmName, firmToken).then((t) => {
      if (!live) return;
      if (t) {
        setTheme(t);
        saveCachedTheme(firmToken || firmName, t);
      }
      setReady(true); // fetch settled either way — mount with what we have
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHost, firmName, firmToken, overrideKey]);

  // Cold-first-visit fallback: never hold the launcher hostage to a slow
  // /chat/config — mount with defaults after 800ms if it hasn't resolved.
  useEffect(() => {
    if (ready) return;
    const t = window.setTimeout(() => setReady(true), 800);
    return () => clearTimeout(t);
  }, [ready]);

  // Warn before leaving the page if a chat is in progress (bubble open or not),
  // so a visitor mid-intake gets a "are you sure?" rather than losing it silently.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveChat(firmName)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [firmName]);

  // Proactive nudge: a rep teaser peeks from the launcher a few seconds in.
  // Suppressed in inline mode, while open, mid-chat, after a session ENDED
  // (single-session lock — nudging into a closed chat is a broken promise),
  // once dismissed this session, when the firm turned the nudge off, or when
  // the message is blank.
  useEffect(() => {
    if (inline || open) return;
    if (!theme.nudge_enabled || !theme.nudge_message?.trim()) return;
    if (hasActiveChat(firmName) || hasEndedSession(firmName)) return;
    try {
      if (sessionStorage.getItem(`acc_nudge_${firmName}`) === "1") return;
    } catch {
      /* sessionStorage may be blocked */
    }
    const t = window.setTimeout(() => setNudge(true), 8000);
    return () => clearTimeout(t);
  }, [inline, open, theme.nudge_enabled, theme.nudge_message, firmName]);

  // Cold first visit: render nothing until the theme settles (fetch resolved
  // or the 800ms fallback fired). All hooks above have already run.
  if (!ready) return null;

  const headerTitle = theme.header_title?.trim() || firmName;
  // No persona NAME in widget chrome (owner decision) — the nudge is signed
  // by the neutral assistant label, not a named rep.
  const nudgeRep = "Live Assistant";
  const nudgeAvatar = theme.avatar_url?.trim() || theme.logo_url?.trim() || null;
  const nudgeInitials = (firmName.replace(/[^a-zA-Z]/g, "").slice(0, 2) || "AC").toUpperCase();

  const posClass = theme.launcher_position === "bl" ? " acc-root-bl" : "";
  const sizeClass = theme.launcher_size === "compact" ? " acc-launcher-compact" : "";
  // The launcher un-hides DURING the minimize (not after unmount) so its
  // delayed fade-in overlaps the window shrinking toward it — the window
  // visibly tucks into the arriving launcher instead of a dead gap.
  const launcherHidden = open && !closing;

  return (
    <div
      class={`acc-root${inline ? " acc-root-inline" : ""}${open ? " acc-root-open" : ""}${closing ? " acc-root-closing" : ""}${posClass}`}
      style={themeCssVars(theme) as preact.JSX.CSSProperties}
    >
      {/* The panel is always rendered when open, animates in via CSS. In inline
          mode it's always open and fills the host container; `resetKey` remounts
          it fresh when the visitor ends a conversation. */}
      {open && (
        <ChatWindow
          key={resetKey}
          chatHost={chatHost}
          firmName={firmName}
          firmToken={firmToken}
          source={source}
          headerTitle={headerTitle}
          welcomeTitle={theme.welcome_title}
          welcomeSubtitle={theme.welcome_subtitle}
          logoUrl={theme.logo_url}
          avatarUrl={theme.avatar_url}
          trustChips={theme.trust_chips}
          starterPrompts={theme.starter_prompts}
          prechatEnabled={theme.prechat_enabled}
          formIntro={theme.form_intro}
          demoToken={demoToken}
          inline={inline}
          onClose={close}
        />
      )}
      {/* Proactive nudge — a rep teaser above the launcher (bubble mode only). */}
      {!inline && nudge && !open && (
        <NudgeBubble
          message={theme.nudge_message}
          repName={nudgeRep}
          avatar={nudgeAvatar}
          initials={nudgeInitials}
          onOpen={openFromNudge}
          onDismiss={dismissNudge}
        />
      )}
      {/* Launcher: pill-shaped button with the firm's icon + label. Bubble mode
          only — inline mode has no floating launcher. Hidden when open. */}
      {!inline && (
        <button
          ref={launcherRef}
          type="button"
          class={`acc-launcher${launcherHidden ? " acc-launcher-hidden" : ""}${sizeClass}`}
          onClick={() => {
            // A click mid-minimize cancels the pending unmount and restores
            // the still-mounted window instead of racing the close timer.
            window.clearTimeout(closeTimer.current);
            setClosing(false);
            setOpen(true);
          }}
          aria-label={`Open chat with ${firmName}`}
          aria-hidden={launcherHidden}
          tabIndex={launcherHidden ? -1 : 0}
        >
          <span class="acc-launcher-icon"><LauncherIcon theme={theme} size={20} /></span>
          <span class="acc-launcher-label">{theme.launcher_label}</span>
        </button>
      )}
    </div>
  );
}

export function mountAcceltraWidget(
  opts: MountOptions,
): { unmount: () => void; update: (next: Partial<MountOptions>) => void } {
  // Validate required options early
  if (!opts.firmName) throw new Error("mountAcceltraWidget: firmName required");
  if (!opts.chatHost) throw new Error("mountAcceltraWidget: chatHost required");

  const mode: "bubble" | "inline" = opts.mode === "inline" ? "inline" : "bubble";
  if (mode === "inline" && !opts.mountTo) {
    throw new Error("mountAcceltraWidget: inline mode requires a mountTo element");
  }

  // Mount target: explicit, or a generated div appended to <body>
  const host = opts.mountTo ?? (() => {
    const d = document.createElement("div");
    d.id = "acceltra-widget";
    document.body.appendChild(d);
    return d;
  })();

  // Shadow DOM for style isolation
  const shadow = host.attachShadow({ mode: "open" });
  const styleEl = document.createElement("style");
  styleEl.textContent = styles;
  shadow.appendChild(styleEl);
  const mountPoint = document.createElement("div");
  // Inline mode fills its host: the mount point must carry height down to
  // .acc-root-inline (which is height:100%), or the panel collapses to content
  // height and leaves empty space below it inside the host element.
  if (mode === "inline") {
    mountPoint.style.height = "100%";
  }
  shadow.appendChild(mountPoint);

  let current = opts;
  const renderApp = (o: MountOptions) => {
    render(
      h(App, {
        firmName: o.firmName,
        firmToken: o.firmToken,
        chatHost: o.chatHost,
        mode,
        startOpen: o.startOpen ?? false,
        demoToken: o.demoToken,
        source: o.source,
        themeOverride: o.theme,
        themeHint: o.themeHint,
      }),
      mountPoint,
    );
  };
  renderApp(current);

  return {
    unmount: () => {
      render(null, mountPoint);
      host.parentNode?.removeChild(host);
    },
    // Live-update a mounted instance (e.g. push a new theme from the dashboard
    // branding editor). Re-renders the same App, so its state (open, started,
    // conversation) is preserved — only the changed props apply.
    update: (next: Partial<MountOptions>) => {
      current = { ...current, ...next };
      renderApp(current);
    },
  };
}

// Auto-mount when loaded as a <script> tag with data-* attributes
function autoMount(): void {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) return;
  const firmName = script.dataset.firmName;
  const firmToken = script.dataset.firmToken;  // preferred firm identity (opaque key)
  const chatHost = script.dataset.chatHost;
  const demoToken = script.dataset.demoToken;  // optional; dashboard only
  if (!firmName || !chatHost) {
    console.warn(
      "[Acceltra] data-firm-name and data-chat-host attributes required for auto-mount",
    );
    return;
  }
  // Launch style: data-mode="inline" + data-mount-to="#selector" pins the panel
  // into a page element (e.g. a website hero); otherwise the floating bubble.
  const mode: "bubble" | "inline" = script.dataset.mode === "inline" ? "inline" : "bubble";
  let mountTo: HTMLElement | undefined;
  if (mode === "inline") {
    const sel = script.dataset.mountTo;
    const el = sel ? document.querySelector(sel) : null;
    if (!el) {
      console.warn(
        `[Acceltra] inline mode needs data-mount-to pointing at an existing element (got ${JSON.stringify(sel)})`,
      );
      return;
    }
    mountTo = el as HTMLElement;
  }
  // Optional brand-color hints emitted by the dashboard's embed generator so
  // even a first visit (no theme cache yet) paints in the firm's colors
  // before /chat/config resolves. Invalid values are ignored.
  const HEX = /^#[0-9a-fA-F]{6}$/;
  let themeHint: Partial<WidgetTheme> | undefined;
  const primaryHint = script.dataset.primaryColor;
  const accentHint = script.dataset.accentColor;
  if (primaryHint && HEX.test(primaryHint)) themeHint = { primary_color: primaryHint };
  if (accentHint && HEX.test(accentHint)) themeHint = { ...themeHint, accent_color: accentHint };
  // Capture source/attribution at load; data-source-* attrs override (e.g. a
  // firm overriding origin on a multi-domain setup).
  const source = captureSource(sourceOverridesFromDataset(script.dataset));
  mountAcceltraWidget({ firmName, firmToken, chatHost, mode, mountTo, demoToken, source, themeHint });
}

// Run on DOMContentLoaded if the document isn't ready yet
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoMount, { once: true });
} else {
  autoMount();
}

// Expose the programmatic API on window for non-module hosts
declare global {
  interface Window {
    AcceltraWidget?: {
      mount: typeof mountAcceltraWidget;
    };
  }
}
window.AcceltraWidget = { mount: mountAcceltraWidget };
